import { FormEvent, useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { useAuth } from "@/auth/AuthContext";

export default function RegisterPage() {
  const { register, status } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();

  const [inviteCode, setInviteCode] = useState("");
  const [username, setUsername] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // URL ?code=XXX 自动回填
  useEffect(() => {
    const code = params.get("code");
    if (code && !inviteCode) setInviteCode(code.trim().toUpperCase());
  }, [params]); // eslint-disable-line react-hooks/exhaustive-deps

  if (status === "authed") {
    const next = (location.state as { from?: string } | null)?.from || "/";
    navigate(next, { replace: true });
    return null;
  }

  const usernameOk = /^[a-zA-Z][a-zA-Z0-9_]{1,31}$/.test(username);
  const pwOk = password.length >= 8;
  const matchOk = password === confirmPw;
  const codeOk = inviteCode.length >= 8;
  const canSubmit = usernameOk && pwOk && matchOk && codeOk && !submitting;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    setSubmitting(true);
    try {
      await register({
        invite_code: inviteCode.trim(),
        username: username.trim(),
        password,
        display_name: displayName.trim() || undefined,
      });
      navigate("/", { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "注册失败");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50 dark:bg-zinc-950 px-4">
      <form
        onSubmit={onSubmit}
        className="w-full max-w-sm rounded-lg border bg-white dark:bg-zinc-900 p-6 shadow-sm space-y-4"
        autoComplete="off"
      >
        <div className="text-center">
          <div className="text-2xl">🐋</div>
          <div className="text-lg font-semibold tracking-wide mt-1">WhaleSeek 寻鲸</div>
          <div className="text-xs text-zinc-500 mt-1">凭邀请码注册</div>
        </div>

        <label className="block">
          <span className="text-xs text-zinc-600 dark:text-zinc-400">邀请码</span>
          <input
            type="text"
            value={inviteCode}
            onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
            placeholder="XXXXXXXXXXXX"
            className="mt-1 w-full rounded border px-3 py-2 bg-white dark:bg-zinc-800 text-sm font-mono tracking-wider"
            required
            autoFocus={!inviteCode}
          />
        </label>

        <label className="block">
          <span className="text-xs text-zinc-600 dark:text-zinc-400">用户名（≥ 2 位，字母开头）</span>
          <input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="username"
            className="mt-1 w-full rounded border px-3 py-2 bg-white dark:bg-zinc-800 text-sm"
            required
          />
          {username && !usernameOk && (
            <div className="text-xs text-rose-500 mt-1">字母开头，仅字母 / 数字 / 下划线，2-32 位</div>
          )}
        </label>

        <label className="block">
          <span className="text-xs text-zinc-600 dark:text-zinc-400">显示名（可选）</span>
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="不填默认与用户名相同"
            className="mt-1 w-full rounded border px-3 py-2 bg-white dark:bg-zinc-800 text-sm"
          />
        </label>

        <label className="block">
          <span className="text-xs text-zinc-600 dark:text-zinc-400">密码（≥ 8 位）</span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            className="mt-1 w-full rounded border px-3 py-2 bg-white dark:bg-zinc-800 text-sm"
            required
          />
        </label>

        <label className="block">
          <span className="text-xs text-zinc-600 dark:text-zinc-400">再输一次密码</span>
          <input
            type="password"
            value={confirmPw}
            onChange={(e) => setConfirmPw(e.target.value)}
            autoComplete="new-password"
            className="mt-1 w-full rounded border px-3 py-2 bg-white dark:bg-zinc-800 text-sm"
            required
          />
          {confirmPw && !matchOk && (
            <div className="text-xs text-rose-500 mt-1">两次密码不一致</div>
          )}
        </label>

        {error && (
          <div className="text-sm text-red-600 bg-red-50 dark:bg-red-950 dark:text-red-300 rounded px-2 py-1">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={!canSubmit}
          className="w-full rounded bg-zinc-900 text-white dark:bg-white dark:text-zinc-900 py-2 text-sm font-medium disabled:opacity-50"
        >
          {submitting ? "提交中…" : "创建账号"}
        </button>

        <div className="text-center text-xs text-zinc-400">
          已有账号？<Link to="/login" className="text-blue-600 hover:underline">去登录</Link>
        </div>
      </form>
    </div>
  );
}
